import React from "react";

const DAY = 86400000;

// expiryDate is a "YYYY-MM-DD" string from the date input. A campaign stays
// live through the end of that day in Bangladesh time (UTC+6), regardless of
// the viewer's timezone. Keep in sync with getAllCampaigns in zapai-frontend.
export const getCampaignExpiryTime = (expiryDate) => {
  if (!expiryDate) return null;
  const time = new Date(`${expiryDate}T23:59:59.999+06:00`).getTime();
  return isNaN(time) ? null : time;
};

// Campaign ids are the creation timestamp (see handleSubmit in allCampaigns)
const getCampaignCreatedTime = (campaign) => {
  const time = Number(campaign.id);
  return time > 1e12 ? time : null;
};

// Derives everything the list needs to render and filter one campaign
export const getCampaignStatus = (campaign, now) => {
  const expiryTime = getCampaignExpiryTime(campaign.expiryDate);
  const createdTime = getCampaignCreatedTime(campaign);
  const remaining = expiryTime ? expiryTime - now : null;
  const expired = remaining !== null && remaining <= 0;
  const endingSoon = remaining !== null && !expired && remaining < DAY;

  let progress = null;
  if (expiryTime && createdTime && expiryTime > createdTime) {
    progress = Math.min(
      100,
      Math.max(0, ((now - createdTime) / (expiryTime - createdTime)) * 100),
    );
  }

  let key = "live";
  if (expired) key = "expired";
  else if (!campaign.visible) key = "inactive";
  else if (endingSoon) key = "ending";

  return { key, expiryTime, remaining, expired, endingSoon, progress };
};

export const STATUS_META = {
  live: { label: "Live", className: "cmp-status-live" },
  ending: { label: "Ending soon", className: "cmp-status-ending" },
  inactive: { label: "Inactive", className: "cmp-status-inactive" },
  expired: { label: "Expired", className: "cmp-status-expired" },
};

const formatExpiryDay = (time) =>
  new Date(time).toLocaleString("en-GB", {
    timeZone: "Asia/Dhaka",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const formatExpiryClock = (time) =>
  new Date(time).toLocaleString("en-GB", {
    timeZone: "Asia/Dhaka",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

export const formatAgo = (ms) => {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

const pad = (n) => String(n).padStart(2, "0");

export const Countdown = ({ remaining, urgent }) => {
  const totalSeconds = Math.floor(remaining / 1000);
  const segments = [
    { value: Math.floor(totalSeconds / 86400), unit: "Days" },
    { value: Math.floor((totalSeconds % 86400) / 3600), unit: "Hrs" },
    { value: Math.floor((totalSeconds % 3600) / 60), unit: "Min" },
    { value: totalSeconds % 60, unit: "Sec" },
  ];
  return (
    <div className={"cmp-countdown" + (urgent ? " cmp-countdown-urgent" : "")}>
      {segments.map((s) => (
        <div className="cmp-countdown-seg" key={s.unit}>
          <span className="cmp-countdown-value">{pad(s.value)}</span>
          <span className="cmp-countdown-unit">{s.unit}</span>
        </div>
      ))}
    </div>
  );
};

const relativeLabel = (status, now) => {
  if (status.expired) return `Ended ${formatAgo(now - status.expiryTime)}`;
  const days = Math.floor(status.remaining / DAY);
  if (days === 0) return "Ends today";
  if (days === 1) return "Ends tomorrow";
  return `Ends in ${days} days`;
};

export const ExpiryDateCell = ({ status, now }) => {
  if (!status.expiryTime) {
    return (
      <div className="cmp-expiry cmp-expiry-none">
        <div className="cmp-cal cmp-cal-none">
          <span className="cmp-cal-day">∞</span>
        </div>
        <div>
          <div className="cmp-expiry-title">No expiry date</div>
          <div className="cmp-expiry-sub">Runs until switched off</div>
        </div>
      </div>
    );
  }

  const date = new Date(status.expiryTime);
  const day = date.toLocaleString("en-GB", {
    timeZone: "Asia/Dhaka",
    day: "2-digit",
  });
  const month = date.toLocaleString("en-GB", {
    timeZone: "Asia/Dhaka",
    month: "short",
  });
  const tone = STATUS_META[status.key].className;

  return (
    <div className={"cmp-expiry " + tone}>
      <div className="cmp-expiry-row">
        <div className="cmp-cal">
          <span className="cmp-cal-month">{month}</span>
          <span className="cmp-cal-day">{day}</span>
        </div>
        <div>
          <div className="cmp-expiry-title">
            {formatExpiryDay(status.expiryTime)}
          </div>
          <div className="cmp-expiry-sub">
            {formatExpiryClock(status.expiryTime)} · BD time
          </div>
          <span className="cmp-chip">{relativeLabel(status, now)}</span>
        </div>
      </div>
      {status.progress !== null && (
        <div className="cmp-progress" title="Campaign duration elapsed">
          <div className="cmp-progress-track">
            <div
              className="cmp-progress-fill"
              style={{ width: `${status.progress}%` }}
            />
          </div>
          <span className="cmp-progress-label">
            {Math.floor(status.progress)}% elapsed
          </span>
        </div>
      )}
    </div>
  );
};

export const StatusCell = ({ campaign, status }) => {
  const meta = STATUS_META[status.key];
  return (
    <div className="cmp-status-cell">
      <span className={"cmp-pill " + meta.className}>
        <span className="cmp-dot" />
        {meta.label}
      </span>

      {status.expired ? (
        <>
          {campaign.visible && (
            <div className="cmp-note cmp-note-warn">
              ⚠ Still switched on, but hidden from the home page. Update the
              expiry date or switch it off.
            </div>
          )}
        </>
      ) : status.expiryTime ? (
        <>
          <Countdown remaining={status.remaining} urgent={status.endingSoon} />
          {!campaign.visible && (
            <div className="cmp-note">Switched off, not shown on home page</div>
          )}
        </>
      ) : (
        <div className="cmp-note">
          {campaign.visible
            ? "No end date, always shown"
            : "Switched off, not shown on home page"}
        </div>
      )}
    </div>
  );
};
